import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback, type MouseEventHandler } from "react";

import { useAssetUrl } from "@skilitics/assets";
import { classNames, FONT_WEIGHT_SEMI_BOLD, Skeleton, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { MediaIcon } from "./MediaIcon";

const width = 234;
const height = 160;

const duration = 150;

const transition = css`
  transition: all ${duration}ms ease-in-out;
`;

const Image = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${width - 2}px;
  height: 126px;

  background-color: ${({ theme }) => theme.surfaceSoft};

  border-start-start-radius: 5px;
  border-start-end-radius: 5px;

  ${transition}
`;

const Thumbnail = styled.span`
  display: block;
  height: 100%;
  width: 100%;

  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: transparent;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 32px;
  width: 100%;

  font-size: ${({ theme }) => theme.fontSize};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  color: ${({ theme }) => theme.generalTextIcon};
  background-color: ${({ theme }) => theme.surfaceVariant};

  width: ${width}px;
  height: ${height}px;

  border-radius: 5px;
  border: ${({ theme }) => `1px solid ${theme.outlineSoft}`};

  cursor: pointer;
  overflow: hidden;
  user-select: none;

  &:hover {
    border: ${({ theme }) => `1px solid ${theme.outlineDark}`};
  }

  &.selected {
    color: ${({ theme }) => theme.primaryStrong};
    border: ${({ theme }) => `1px solid ${theme.primary}`};
  }

  &.selected ${Image} {
    background-color: ${({ theme }) => theme.primaryContainer};
    position: relative;
  }

  &.selected ${Title} {
    color: ${({ theme }) => theme.primary};
  }

  &.selected ${MediaIcon} {
    filter: grayscale(0%);
  }

  &.unavailable {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }

  ${transition}
`;

type Props = {
  title: string;
  selected: boolean;
  unavailable: boolean;
  thumbnailAssetId?: string;
  onSelect: () => void;
  onConfirm: () => void;
};

export const TemplateCard = ({
  title,
  selected,
  unavailable,
  thumbnailAssetId,
  onSelect,
  onConfirm,
  ...props
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const url = useAssetUrl(thumbnailAssetId);

  const image = url ? (
    <Thumbnail style={{ backgroundImage: `url(${url})` }} />
  ) : (
    <MediaIcon mediaIconType="ComposedScreens" />
  );

  const handleSelect: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      onSelect();
    },
    [onSelect],
  );

  const handleConfirm: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      onConfirm();
    },
    [onConfirm],
  );

  const tooltip = unavailable ? formatMessage("unavailable") : "";

  return (
    <Tooltip title={tooltip} placement="top">
      <div>
        <Container
          className={classNames({ selected, unavailable })}
          onClick={handleSelect}
          onDoubleClick={handleConfirm}
          {...props}
        >
          <Image>{image}</Image>
          <Title>{title}</Title>
        </Container>
      </div>
    </Tooltip>
  );
};

export const TemplateCardSkeleton = () => (
  <Skeleton variant="rounded" width={width} height={height} data-test="template-card-skeleton" />
);

const formatStrings = {
  unavailable: {
    en: "Not available - unable to place a group inside a group.",
  },
} satisfies FormatMessages;
